/**
 * Top-level-node segmenting for the Post Stream Review toolbar (the `block` /
 * gate-OFF strategy).
 *
 * Derives segments directly from what the AI committed, rather than the
 * prosemirror-changeset `block` diff (whose grouping mis-attributes the original
 * side of structural edits). Each overlap-merged coarse segment carries
 * `slices[0]` (original) and `slices[appliedIndex]` (current applied) for one
 * edited region; we compare their top-level nodes and emit one segment per
 * added / removed / updated node.
 *
 * The applied slice occupies exactly `[startPos, endPos]`, so each new node's
 * live range is found by walking node sizes from the first node's true live
 * start (`startPos - appliedSlice.openStart`; see the walk below). Removals are
 * zero-width, anchored where the node was removed. Granularity is top-level only.
 */
import { Fragment, type Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';

import type { AIContentPositionEntry } from '../../../../pm-plugins/ai-session-context/types';
import { isReviewMomentEnabledNoExposure } from '../../../utils/review-moment-enabled';
import { appliedSliceOf, originalSliceOf } from './segmentSlices';

/**
 * Wrap a single complete *block* node in a zero-open slice so per-node undo is
 * `replaceRange(nodeStart, nodeEnd, Slice(node, 0, 0))`.
 *
 * Do not use this for inlines. `doc.slice(inner)` of a textblock is already
 * closed (`openStart`/`openEnd` = 0) because both cuts share that textblock —
 * there is no open depth to preserve. Closing each text node then applying it
 * on the wrapper range replaces the textblock with a paragraph.
 */
const wholeNodeSlice = (node: PMNode): Slice => new Slice(Fragment.from(node), 0, 0);

/** The top-level nodes carried by a slice, in order. */
const topLevelNodes = (slice: Slice): PMNode[] => {
	const nodes: PMNode[] = [];
	slice.content.forEach((node) => nodes.push(node));
	return nodes;
};

/**
 * Derive the reviewable top-level-node segments for the PSR toolbar from the
 * overlap-merged coarse segments. Returns segments in new-document reading order
 * (ascending `startPos`). Unchanged top-level nodes produce no segment.
 *
 * For each coarse segment we align its original and new top-level nodes by
 * trimming the common prefix / suffix (so unchanged surrounding nodes are
 * skipped) and pairing the changed middle positionally:
 *   - paired nodes that differ → `update`,
 *   - extra new nodes          → `add`,
 *   - extra original nodes     → `remove`.
 */
export const calculateTopLevelNodeSegments = (
	coarseSegments: AIContentPositionEntry[],
): AIContentPositionEntry[] => {
	const segments: AIContentPositionEntry[] = [];

	for (const coarse of coarseSegments) {
		// [CCI-17658] Read the coarse entry through its slice history: the original
		// side is `slices[0]`, the "new" side is the currently-applied slice.
		const originalSlice = originalSliceOf(coarse);
		const appliedSlice = appliedSliceOf(coarse);
		const originalNodes = topLevelNodes(originalSlice);
		const newNodes = topLevelNodes(appliedSlice);

		// A taskItem / paragraph `doc.slice(inner)` is a closed run of
		// inline text nodes. Walking them as top-level nodes emits one
		// `wholeNodeSlice(text)` per mark run. Keep the coarse slice (already
		// paired with its replace range at commit) as a single segment.
		const originalHasInline = originalNodes.some((n) => n.isInline);
		const newHasInline = newNodes.some((n) => n.isInline);
		// No-exposure: this runs while the Review moment is being computed, after the
		// canonical exposure has already fired at the gate-enforcement point.
		if ((originalHasInline || newHasInline) && isReviewMomentEnabledNoExposure()) {
			const kind: AIContentPositionEntry['kind'] =
				originalSlice.content.size === 0
					? 'add'
					: appliedSlice.content.size === 0
						? 'remove'
						: 'update';
			segments.push({
				startPos: coarse.startPos,
				endPos: coarse.endPos,
				slices: [originalSlice, appliedSlice],
				appliedIndex: 1,
				kind,
			});
			continue;
		}

		// Trim the common prefix, then the common suffix, so only the genuinely
		// changed nodes remain in the middle. Node equality is exact (`.eq`), so an
		// attribute-only change (e.g. panel type) still counts as a change.
		let prefix = 0;
		while (
			prefix < originalNodes.length &&
			prefix < newNodes.length &&
			originalNodes[prefix].eq(newNodes[prefix])
		) {
			prefix++;
		}
		let suffix = 0;
		while (
			suffix < originalNodes.length - prefix &&
			suffix < newNodes.length - prefix &&
			originalNodes[originalNodes.length - 1 - suffix].eq(newNodes[newNodes.length - 1 - suffix])
		) {
			suffix++;
		}

		const originalMiddle = originalNodes.slice(prefix, originalNodes.length - suffix);
		const newMiddle = newNodes.slice(prefix, newNodes.length - suffix);

		// Walk the live-doc cursor through every new node (prefix included) so each
		// changed node lands at its true `[startPos, endPos]`.
		//
		// The cursor base is `startPos - appliedSlice.openStart`, not `startPos`.
		// When the applied slice was inserted with an open left boundary
		// (`openStart > 0` — the pure-add case), the recorded `startPos` was mapped
		// with a `+1`-per-open-level bias, so it sits *inside* the opened boundary.
		// A fully-closed slice's first node begins `openStart` positions earlier, so
		// walking full node sizes from `startPos` overshoots every emitted range by
		// `openStart`. Subtracting it anchors the first node at its true live start;
		// closed slices (`openStart === 0`, the update case) are unaffected.
		let cursor = coarse.startPos - appliedSlice.openStart;
		for (let i = 0; i < prefix; i++) {
			cursor += newNodes[i].nodeSize;
		}

		const paired = Math.min(originalMiddle.length, newMiddle.length);

		// Paired middle nodes → `update` (skip any that happen to be identical).
		for (let i = 0; i < paired; i++) {
			const newNode = newMiddle[i];
			const startPos = cursor;
			const endPos = cursor + newNode.nodeSize;
			cursor = endPos;
			if (originalMiddle[i].eq(newNode)) {
				continue;
			}
			segments.push({
				startPos,
				endPos,
				slices: [wholeNodeSlice(originalMiddle[i]), wholeNodeSlice(newNode)],
				appliedIndex: 1,
				kind: 'update',
			});
		}

		// Extra new nodes → `add`.
		for (let i = paired; i < newMiddle.length; i++) {
			const newNode = newMiddle[i];
			const startPos = cursor;
			const endPos = cursor + newNode.nodeSize;
			cursor = endPos;
			segments.push({
				startPos,
				endPos,
				slices: [Slice.empty, wholeNodeSlice(newNode)],
				appliedIndex: 1,
				kind: 'add',
			});
		}

		// Extra original nodes → `remove` (zero-width in the live doc, anchored at
		// the cursor — i.e. immediately after the paired/added content).
		for (let i = paired; i < originalMiddle.length; i++) {
			segments.push({
				startPos: cursor,
				endPos: cursor,
				slices: [wholeNodeSlice(originalMiddle[i]), Slice.empty],
				appliedIndex: 1,
				kind: 'remove',
			});
		}
	}

	return segments.sort((a, b) => a.startPos - b.startPos);
};
